
import { Component, Vue, Watch } from 'vue-property-decorator'
import { commonModule, paymentsModule, companyModule, expensesModule, installationsModule, jobsModule, filterModule } from '@/store/modules/store-accessor'
import FilterDropdown from '../components/dropdown/filter/FilterDropdown.vue'
import PaymentFilter from '../components/Filter/PaymentFilter.vue'
import { resolveInitialPaymentDateRange } from '@/helpers/filterPersistence';
import { formatAmount, sortTableDates, pagesExportLimit, templatesFilenameLanguage, mergedAddressAndArea, formatDateWithoutSeconds, getOptimalTextColor } from "@/helpers/data";
import dayjs from 'dayjs';
import Dropdown from '../components/dropdown/Dropdown.vue'
import DropdownItem from '../components/dropdown/DropdownItem.vue'
import FileSaver from 'file-saver';
import InstallationModule from '@/store/modules/installations-module';
import ExportMenu from "@/components/ExportMenu/ExportMenu.vue";
import { openInstallationSideview } from "@/helpers/installationSideview";

@Component({
  components: { FilterDropdown, PaymentFilter, Dropdown, DropdownItem, ExportMenu }
})
export default class Payments extends Vue {
  tableheaders: any [] = [
    { text: this.$t("label"), sortable: true, class: 'row-style', value: 'label_name' },
    { text: this.$t("type"), sortable: true, class: 'row-style', value: 'finc_type' },
    { text: this.$t("client"), sortable: true, class: 'row-style', value: 'client_full_name' },
    { text: this.$t("address"), sortable: true, class: 'row-style', value: 'address' },
    { text: this.assigneeLabel, sortable: true, class: 'row-style', value: 'assignee_id' },
    { text: this.$t("created_at"), sortable: true, class: 'row-style', value: 'created_at', sort: (a: string, b: string) => sortTableDates(a, b) },
    { text: this.$t("amount"), sortable: true, class: 'row-style', value: 'amount', align: 'center'},
    { text: this.$t("payment_type"), sortable: true, class: 'row-style', value: 'payment_type_id' },
    { text: this.$t("notes"), sortable: true, class: 'row-style', value: 'notes' },
    { text: '', align: 'right', sortable: false, class: 'row-style', value: 'id' }
  ]

  model: any = {
    search: '',
    selected: [] as string[]
  }

  isDownloadingPaymentsExport = false

  get assigneeLabel() {
    if(companyModule.company?.modules?.includes('DFB_PORTAL_PARTNERS')) {
      return this.$i18n.t('userPartner');
    }
    else {
      return this.$i18n.t('user');
    }
  }

  get totalPayments() {
    return paymentsModule.paymentsTotals?.payments_total
  }

  get totalPendings() {
    return paymentsModule.paymentsTotals?.total_pending
  }

  get totalExpenses() {
    return paymentsModule.paymentsTotals.expenses_total;
  }

  get payments() {
    return paymentsModule.payments
  }

  get paymentssLength() {
    return this.payments.length;
  }

  get searchLabel() {
    return this.$t("paymentsSearch")
  }

  get paymentsLength() {
    return paymentsModule.payments.length
  }

  formatBalance(balance: number) {
    return formatAmount(balance);
  }

  formatPaymentsTotals() {
    const cashTotal = `${this.$t('cash')}: ${(paymentsModule.paymentsTotals as any)?.payments_total_cash ? `${this.formatBalance((paymentsModule.paymentsTotals as any)?.payments_total_cash)}€` : '-'}`;
    const creditCardTotal = `${this.$t('card')}: ${(paymentsModule.paymentsTotals as any)?.payments_total_credit_card ? `${this.formatBalance((paymentsModule.paymentsTotals as any)?.payments_total_credit_card)}€` : '-'}`
    const bankTransferTotal = `${this.$t('bankTransfer')}: ${(paymentsModule.paymentsTotals as any)?.payments_total_bank_transfer ? `${this.formatBalance((paymentsModule.paymentsTotals as any)?.payments_total_bank_transfer)}€` : '-'}`
    const checkTotal = `${this.$t('check')}: ${(paymentsModule.paymentsTotals as any)?.payments_total_check ? `${this.formatBalance((paymentsModule.paymentsTotals as any)?.payments_total_check)}€` : '-'}`
    const creditsTotal = `${this.$t('credit')}: ${(paymentsModule.paymentsTotals as any)?.payments_total_credit ? `${this.formatBalance((paymentsModule.paymentsTotals as any)?.payments_total_credit)}€` : '-'}`

    return {
      cashTotal: cashTotal,
      creditCardTotal: creditCardTotal,
      bankTransferTotal: bankTransferTotal,
      checkTotal: checkTotal,
      creditsTotal: creditsTotal,
    }
  }

  async mounted () {
    /*
     TODO: Handle installations properly when UX is clear.
     */

    // Clear the search input and all of the other filters when visiting the page
    paymentsModule.setSearch('');
    paymentsModule.setFilters('');
    paymentsModule.setQueryObject({});

    // Honour the persisted date filter for this page; editors are clamped to one day.
    const { from: dateFrom, to: dateTo } = resolveInitialPaymentDateRange();

    paymentsModule.getPaymentsAction(`?date_from=${dateFrom}&date_to=${dateTo}`);
    commonModule.getPersonnelPartnersCombo();
    commonModule.initSnackbar({});
    commonModule.getPaymentTypeCombo();
    commonModule.getExpenseLabelsCombo();
    paymentsModule.getPaymentsTotals(`?date_from=${dateFrom}&date_to=${dateTo}`);
    paymentsModule.setFilters(`?date_from=${dateFrom}&date_to=${dateTo}`);

    paymentsModule.queryObject.date_from = dateFrom;
    paymentsModule.queryObject.date_to = dateTo;
    paymentsModule.setFilters(`?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  destroyed() {
    paymentsModule.clearPayments();
  }

  get isLoading() {
    return paymentsModule.isLoadingTable;
  }

  async refreshData() {
    paymentsModule.setIsLoadingTable(true);
    await paymentsModule.getPaymentsAction(paymentsModule.filters);
    await paymentsModule.getPaymentsTotals(paymentsModule.filters);
    await filterModule.getPaymentsAndExpensesFilters();
    paymentsModule.setIsLoadingTable(false);
  }

  async editItem(item: Record<string, any>) {
    if (this.isCreditPayment(item)) return;
    if(item.finc_type === 'EXPENSE') {
      commonModule.showModal({ name: 'edit-expense-modal', payload: { expense: item }})
    } else {
      const installation = await installationsModule.getInstallationDetailsInfo(item.installation_id);
      const job = await jobsModule.getJobDetails(item.job_id);
      commonModule.showModal({ name: 'edit-payment-modal', payload: { payment: item, installation: installation, job: (job as any).jobDetails[0] }})
    }
  }

  isCreditPayment(item: Record<string, any>): boolean {
    return item.source === "CREDIT" || item.credit_ledger_id != null || (item.finc_type === "PAYMENT" && item.payment_type_id == null);
  }

  paymentTypeIcon(item: Record<string, any>) {
    if (this.isCreditPayment(item)) return "mdi-cash-sync";
    if (item.payment_type_id === 1) return "mdi-cash";
    if (item.payment_type_id === 2) return "mdi-credit-card-outline";
    if (item.payment_type_id === 3) return "mdi-bank-outline";
    if (item.payment_type_id === 4) return "mdi-checkbook";
    return "";
  }

  deleteItem(item: Record<string, any>) {
    if(item.finc_type === 'EXPENSE') {
      commonModule.showModal({ name: 'delete-expense-modal', payload: { expense: item }})
    } else {
      commonModule.showModal({ name: 'delete-payment-modal', payload: { payment: item }})
    }
  }

  async downloadExpenseFile(expense: Record<string, any>) {
    let downloadFile = await expensesModule.getExpensionFile(expense.id);
    downloadFile = new File([downloadFile], expense.file_name);
    FileSaver.saveAs(downloadFile);
  }

  timer: number;
  onSearchChange(val: string) {
    clearTimeout(this.timer)

    this.timer = setTimeout(async () => {
      paymentsModule.setIsLoadingTable(true);
      if (val && val.length > 0) {
        paymentsModule.setSearch(val);
        paymentsModule.queryObject.search = paymentsModule.search;
      }
      else {
        paymentsModule.setSearch('');
        delete paymentsModule.queryObject.search;
      }
      this.$router.replace({ query: paymentsModule.queryObject });

      // Check if user selected any of the queries
      const queryIndex = this.$route.fullPath.indexOf('?');
      if (queryIndex >= 0) {
        const query = this.$route.fullPath.substring(queryIndex);
        paymentsModule.setFilters(query);
      } else {
        paymentsModule.setFilters('');
      }

      await paymentsModule.getPaymentsAction(paymentsModule.filters);
      await paymentsModule.getPaymentsTotals(paymentsModule.filters);
      paymentsModule.setIsLoadingTable(false);
    }, 500);
  }

   downloadExportLengthCheck(type: string) {
    if(type === 'pdf') {
      return this.paymentsLength > 0 && this.paymentsLength <= pagesExportLimit();
    }
    return this.paymentsLength > 0;
  }

  async donwloadPaymentsExport(format: string) {
    this.isDownloadingPaymentsExport = true;
    const userTimezone = dayjs.tz.guess();

    const data = { 'user_timezone': userTimezone, 'language': localStorage.getItem('lang'), format: format };
    const filetype = format === 'pdf' ? 'pdf' : 'xlsx'

    const company = await companyModule.getCompanyInfo();
    const filename = `${templatesFilenameLanguage()}_${company.entity_name}_DfB_finances_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.${filetype}`.replace(/ /g, '_');
    const pdf = await  paymentsModule.getPaymentsPdfExportAction({ query: paymentsModule.filters, data: data });
    const file = new File([pdf], filename);
    FileSaver.saveAs(file);

    this.isDownloadingPaymentsExport = false;
  }

  setMergedAddressAndArea(address: string, area: string) {
    return mergedAddressAndArea(address, area);
  }

  async openInstallationSideView(item: Record<string, any>) {
    paymentsModule.setIsLoadingTable(true);

    if(item.installation_id) {
      try {
        await openInstallationSideview({
          installationId: item.installation_id,
          tab: 'jobs',
          jobId: item.job_id,
        });
      } catch (error) {
        // Sideview open failed (e.g. installation deleted).
      }
    } else { // company expense
      expensesModule.unsetCurrentExpense();
      await expensesModule.getExpenseDetails(item.id);
      commonModule.showModal({ name: 'company-expense-modal'});

    }
    paymentsModule.setIsLoadingTable(false);
  }

  formatReadableDate(createdAt: Date) {
    return formatDateWithoutSeconds(createdAt);
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

}
